import { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Grid,
  Autocomplete,
  CircularProgress,
  Snackbar,
  Alert,
  duration,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { FilePenLine, Check } from "lucide-react";
import {
  getStreams,
  getInstitutes,
  getAffiliations,
} from "../../api/AcademicAPI";
import { getCountries, getStates, getCities } from "../../api/ProfileAPI";
import ConfirmDialog from "../common/ConfirmDialog";

const AddEducationModal = ({
  open,
  onClose,
  onSave,
  existingLevels,
  allLevels,
  editData = null,
  academicData,
}) => {
  console.log("ACADEMIC DATA", academicData);
  console.log("AL", allLevels);
  const initialFormState = {
    level: "",
    qualificationName: "",
    stream: "",
    duration: "",
    institutionName: "",
    institutionId: "",
    affiliationId: "",
    customInstitute: "",
    affiliation: "",
    educationStatus: "",
    percentage: "",
    numberOfBacklogs: "",
    yearOfPassing: "",
    country: "",
    state: "",
    city: "",
    scoreType: "",
  };
  const [formData, setFormData] = useState(initialFormState);
  const [streams, setStreams] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingStreams, setLoadingStreams] = useState(false);
  const [institutes, setInstitutes] = useState([]);
  const [instPage, setInstPage] = useState(1);
  const [instHasMore, setInstHasMore] = useState(true);
  const [loadingInstitutes, setLoadingInstitutes] = useState(false);
  const [affiliations, setAffiliations] = useState([]);
  const [affPage, setAffPage] = useState(1);
  const [hasMoreAff, setHasMoreAff] = useState(true);
  const [loadingAff, setLoadingAff] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [searchInsQuery, setSearchInsQuery] = useState("");
  const [searchAffQuery, setSearchAffQuery] = useState("");
  const [customInstitute, setCustomInstitute] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [scoreType, setScoreType] = useState(1); // 1 = % , 2 = CGPA
  const highestLevelId = Number(localStorage.getItem("highestLevelId"));

  console.log("HLI", highestLevelId);

  const normalizedLevels = useMemo(() => {
    const schoolIds = [62, 8, 60, 66]; // gradlynk_id values for school
    return allLevels.map((l) => ({
      ...l,
      category: schoolIds.includes(l.gradlynk_id) ? "school" : "ug",
    }));
  }, [allLevels]);

  const [isUGLevel, setIsUGLevel] = useState(true);
  // Utility: get course level name by ID
  const getLevelName = (id) => {
    const level = allLevels.find((l) => l.gradlynk_id === id);
    return level ? level.course_level : id;
  };

  useEffect(() => {
    if (editData) {
      const mappedData = {
        level: editData.highest_level_id,
        qualificationName: editData.gl_qualification_name || "",
        stream: editData.gl_stream_id || "",
        duration: editData.course_duration || "",
        institutionId: editData.gl_school_university_id || "",
        institutionName: editData.university_name || "",
        affiliationId: editData.gl_affiliation_id || "",
        affiliation: editData.affiliation_name || "",
        educationStatus: editData.education_status,
        percentage: editData.percentage || "",
        numberOfBacklogs: editData.number_of_backlogs || "",
        yearOfPassing: editData.year_of_passing || "",
        country: editData.country || "",
        state: editData.state || "",
        city: editData.city || "",
        scoreType: editData.marks_type || "",
      };

      // Only update if something actually changed
      setFormData((prev) =>
        JSON.stringify(prev) === JSON.stringify(mappedData) ? prev : mappedData
      );

      const selectedLevel = normalizedLevels.find(
        (l) => l.gradlynk_id === editData.highest_level_id
      );
      setIsUGLevel(selectedLevel?.category === "ug");
    } else {
      setFormData(initialFormState);
      setIsUGLevel(true);
    }
  }, [editData, normalizedLevels]);

  const validateForm = () => {
    let errors = {};
    const {
      // level,
      qualificationName,
      stream,
      duration,
      institutionName,
      customInstitute,
      affiliation,
      educationStatus,
      scoreType,
      percentage,
      yearOfPassing,
      country,
      state,
    } = formData;

    // if (!level) errors.level = true;
    if (!stream) errors.stream = true;
    if (!educationStatus) errors.educationStatus = true;
    if (!scoreType) errors.scoreType = true;
    if (!percentage) errors.percentage = true;
    if (!yearOfPassing) errors.yearOfPassing = true;
    if (!country) errors.country = true;
    if (!state) errors.state = true;

    if (isUGLevel) {
      if (!qualificationName) errors.qualificationName = true;
      if (!duration) errors.duration = true;
      if (!institutionName) errors.institutionName = true;
      if (!affiliation) errors.affiliation = true;
      // if (formData.numberOfBacklogs === "" || Number(formData.numberOfBacklogs) < 0) {
      //   errors.numberOfBacklogs = true;
      // }
      // if (!numberOfBacklogs) errors.numberOfBacklogs = true;
    } else {
      if (!institutionName) errors.institutionName = true;
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };
  const handleSaveClick = () => {
    if (!validateForm()) {
      setErrorMessage("Please fill all the required fields.");
      return;
    }
    setShowConfirm(true);
  };

  const handleConfirm = () => {
    const finalFormData = {
      ...formData,
      //scoreType: formData.scoreType,
      institutionId:
        formData.institutionName === "Others" ? -1 : formData.institutionId,
      affiliationId: isUGLevel ? formData.affiliationId : null,
      institutionName:
        formData.institutionName === "Others"
          ? customInstitute
          : formData.institutionName,
      stream: formData.stream,
      duration: isUGLevel ? formData.duration : null,
      qualificationName: isUGLevel ? formData.qualificationName : null,
      numberOfBacklogs: formData.numberOfBacklogs || null,
      city: formData.city || null,
    };

    console.log("FINAL FORM DATA >>>", finalFormData);
    onSave(finalFormData, editData);
    setShowConfirm(false);
  };

  const handleCancel = () => {
    setShowConfirm(false); // Only close confirm, keep form open
  };

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };
  const fetchCountries = async () => {
    setLoadingCountries(true);
    try {
      const res = await getCountries();
      if (res.success) setCountries(res.data);
    } catch (err) {
      console.error("Error fetching countries:", err);
    } finally {
      setLoadingCountries(false);
    }
  };
  const fetchStreams = async (pageNum = 1) => {
    if (loadingStreams || !hasMore) return;
    setLoadingStreams(true);
    try {
      const response = await getStreams(pageNum);
      if (response.success) {
        setStreams((prev) => [...prev, ...response.data]);
        setHasMore(!!response.pagination?.nextPage);
        setPage(pageNum);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching streams:", error);
    } finally {
      setLoadingStreams(false);
    }
  };

  const fetchAffiliations = async (pageNum = 1, searchTerm = "") => {
    if (loadingAff || !hasMoreAff) return;
    setLoadingAff(true);
    try {
      const res = await getAffiliations(pageNum, searchTerm);
      if (res.success) {
        setAffiliations((prev) => [...prev, ...res.data]);
        setHasMoreAff(!!res.pagination?.nextPage);
        setAffPage(pageNum);
      } else {
        setHasMoreAff(false);
      }
    } catch (err) {
      console.error("Error fetching affiliations:", err);
    } finally {
      setLoadingAff(false);
    }
  };
  const getInstituteType = () => (isUGLevel ? "1" : "3");

  const fetchInstitutes = async (pageNum = 1, type, university_name = "") => {
    if (loadingInstitutes || !instHasMore) return;
    setLoadingInstitutes(true);

    try {
      const response = await getInstitutes(pageNum, type, university_name);
      if (response.success) {
        setInstitutes((prev) => [...prev, ...response.data]);
        setInstHasMore(!!response.pagination?.nextPage);
        setInstPage(pageNum);
      } else {
        setInstHasMore(false);
      }
    } catch (err) {
      console.error("Error fetching institutes:", err);
    } finally {
      setLoadingInstitutes(false);
    }
  };

  const handleStreamScroll = (event) => {
    const bottom =
      event.target.scrollHeight - event.target.scrollTop <=
      event.target.clientHeight + 10;
    if (bottom && hasMore && !loadingStreams) {
      fetchStreams(page + 1);
    }
  };

  const fetchStates = async () => {
    setLoadingStates(true);
    try {
      const res = await getStates(formData.country); // pass country ID
      if (res.success) setStates(res.data);
    } catch (err) {
      console.error("Error fetching states:", err);
    } finally {
      setLoadingStates(false);
    }
  };
  useEffect(() => {
    if (!formData.country) {
      setStates([]);
      setCities([]);
      return;
    }

    fetchStates();
  }, [formData.country]);

  const fetchCities = async () => {
    setLoadingCities(true);
    const payload = { country_id: formData.country, state_id: formData.state };
    try {
      const res = await getCities(payload);
      if (res.success) setCities(res.data);
    } catch (err) {
      console.error("Error fetching cities:", err);
    } finally {
      setLoadingCities(false);
    }
  };

  useEffect(() => {
    if (!formData.state || !formData.country) {
      setCities([]);
      return;
    }
    fetchCities();
  }, [formData.state, formData.country]);

  useEffect(() => {
    setStreams([]);
    setPage(1);
    setHasMore(true);
    fetchStreams(1);
    setAffiliations([]);
    setAffPage(1);
    setHasMoreAff(true);
    fetchAffiliations(1);
    fetchCountries();
  }, []);

  useEffect(() => {
    setInstitutes([]);
    setInstPage(1);
    setInstHasMore(true);
    fetchInstitutes(1, getInstituteType());
  }, [isUGLevel]);

  //  Filter available levels by gradlynk_id
  const availableLevels = normalizedLevels.filter((level) => {
    //  Always hide highest completed level
    if (
      Array.isArray(academicData) &&
      academicData.length > 0 &&
      level.gradlynk_id === highestLevelId
    ) {
      return false;
    }

    //  Edit mode → allow only current level
    if (editData) {
      return (
        !existingLevels.includes(level.gradlynk_id) ||
        level.gradlynk_id === editData.highest_level_id
      );
    }

    //  Add mode
    return !existingLevels.includes(level.gradlynk_id);
  });

  console.log("al", availableLevels);

  const ugLevels = availableLevels.filter((level) => level.category === "ug");
  const schoolLevels = availableLevels.filter(
    (level) => level.category === "school"
  );

  const handleInputChange = (field, value) => {
    // ✅ If level changes → reset everything
    if (field === "level") {
      const selectedLevel = normalizedLevels.find(
        (l) => l.gradlynk_id === value
      );

      setIsUGLevel(selectedLevel?.category === "ug");

      setFormData({
        ...initialFormState,
        level: value, // keep selected level
        scoreType: 1, // default score type (optional)
      });

      // reset dependent UI states
      setCustomInstitute("");
      setSearchInsQuery("");
      setSearchAffQuery("");
      setFieldErrors({});
      setErrorMessage("");

      // reset paginated lists
      setInstitutes([]);
      setInstPage(1);
      setInstHasMore(true);

      setAffiliations([]);
      setAffPage(1);
      setHasMoreAff(true);

      return;
    }

    // Normal field update
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (fieldErrors[field]) {
      setFieldErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const educationStatuses = [
    { id: 1, name: "Complete" },
    { id: 2, name: "Pursuing" },
    { id: 3, name: "Awaiting Results" },
  ];
  const renderLabel = (label) => (
    <>
      {label} <span style={{ color: "red" }}>*</span>
    </>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          className: "rounded-lg",
        }}
      >
        <DialogTitle className="bg-[#572575] font-siromi text-xl font-medium text-white flex justify-between items-center">
          <span className="flex gap-2">
            <FilePenLine className="bg-[#FFFFFF26] p-1 rounded-md" />{" "}
            {editData ? "Edit Academic Details" : "Academic Details"}
          </span>
          <IconButton onClick={onClose} className="text-white">
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent className="p-6">
          <Grid container spacing={3} className="mt-2">
            {/* LEVEL */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>{renderLabel("Level")}</InputLabel>
                <Select
                  value={formData.level}
                  onChange={(e) => handleInputChange("level", e.target.value)}
                  label="Level *"
                  disabled={!!editData}
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "#9D0385",
                      fontWeight: "bold",
                    },
                  }}
                >
                  {[...ugLevels, ...schoolLevels].map((level) => (
                    <MenuItem key={level.gradlynk_id} value={level.gradlynk_id}>
                      {level.course_level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* QUALIFICATION NAME for UG/PG */}
            {isUGLevel && (
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={renderLabel("Qualification Name")}
                  value={formData.qualificationName}
                  onChange={(e) =>
                    handleInputChange("qualificationName", e.target.value)
                  }
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "#9D0385",
                      fontWeight: "bold",
                    },
                  }}
                />
              </Grid>
            )}

            {/* STREAM */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>{renderLabel("Stream")}</InputLabel>
                <Select
                  value={formData.stream}
                  onChange={(e) => handleInputChange("stream", e.target.value)}
                  MenuProps={{
                    PaperProps: {
                      style: { maxHeight: 200 },
                      onScroll: handleStreamScroll,
                    },
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "#9D0385",
                      fontWeight: "bold",
                    },
                  }}
                >
                  {streams.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.stream_name}
                    </MenuItem>
                  ))}
                  {loadingStreams && (
                    <MenuItem disabled>Loading more...</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Grid>

            {/* SCHOOL NAME for school category */}
            {!isUGLevel && (
              <>
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    value={formData.institutionName || null}
                    onChange={(e, newValue) => {
                      if (!newValue) {
                        handleInputChange("institutionName", "");
                        return;
                      }
                      if (newValue.id === -1) {
                        handleChange("institutionId", null); // custom name will go in customInstitute
                        handleChange("institutionName", "Others"); // only for UI display
                      } else {
                        handleChange("institutionId", newValue.id); // store ID
                        handleChange(
                          "institutionName",
                          newValue.university_name
                        ); // for UI
                      }
                    }}
                    inputValue={searchInsQuery}
                    onInputChange={(e, newInputValue, reason) => {
                      setSearchInsQuery(newInputValue);
                      if (reason === "input") {
                        setInstitutes([]);
                        setInstPage(1);
                        setInstHasMore(true);
                        fetchInstitutes(1, getInstituteType(), searchInsQuery);
                      }
                    }}
                    // 👇 Append "Others" as a real option at the end
                    options={[
                      ...institutes,
                      { university_name: "Others", id: -1 },
                    ]}
                    isOptionEqualToValue={(option, value) =>
                      typeof value === "string"
                        ? option.university_name === value
                        : option.id === value?.id
                    }
                    getOptionLabel={(option) =>
                      typeof option === "string"
                        ? option
                        : option.university_name
                    }
                    filterOptions={(x) => x}
                    loading={loadingInstitutes}
                    ListboxProps={{
                      style: {
                        maxHeight: 200,
                        overflowY: "auto",
                        position: "relative",
                      },
                      onScroll: (event) => {
                        const { scrollTop, scrollHeight, clientHeight } =
                          event.currentTarget;
                        if (
                          scrollHeight - scrollTop <= clientHeight + 5 &&
                          instHasMore &&
                          !loadingInstitutes
                        ) {
                          fetchInstitutes(instPage + 1, getInstituteType());
                        }
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={renderLabel("School")}
                        placeholder="Search school..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingInstitutes ? (
                                <CircularProgress size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={{
                          "& .MuiInputBase-input": {
                            color: "#9D0385",
                            fontWeight: "bold",
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      const isOthers = option.id === -1;
                      return (
                        <li
                          {...props}
                          key={option.id}
                          style={
                            isOthers
                              ? {
                                  position: "sticky",
                                  bottom: -8,
                                  background: "#fff",
                                  zIndex: 1,
                                }
                              : {}
                          }
                        >
                          {option.university_name}
                        </li>
                      );
                    }}
                  />
                </Grid>
                {formData.institutionName === "Others" && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      className=""
                      label={renderLabel("Enter School Name")}
                      fullWidth
                      value={customInstitute}
                      onChange={(e) => setCustomInstitute(e.target.value)}
                      sx={{
                        "& .MuiInputBase-input": {
                          color: "#9D0385",
                          fontWeight: "bold",
                        },
                      }}
                    />
                  </Grid>
                )}
              </>
            )}

            {/* DURATION, INSTITUTION, AFFILIATION for UG */}
            {isUGLevel && (
              <>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth>
                    <InputLabel>{renderLabel("Duration")}</InputLabel>
                    <Select
                      value={formData.duration}
                      onChange={(e) =>
                        handleInputChange("duration", e.target.value)
                      }
                      sx={{
                        "& .MuiInputBase-input": {
                          color: "#9D0385",
                          fontWeight: "bold",
                        },
                      }}
                    >
                      {[1, 2, 3, 4, 5, 6].map((year) => (
                        <MenuItem key={year} value={year}>
                          {year} Year{year > 1 ? "s" : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={4}>
                  <Autocomplete
                    value={formData.institutionName || null}
                    onChange={(e, newValue) => {
                      if (!newValue) {
                        handleInputChange("institutionName", "");
                        return;
                      }
                      if (newValue.id === -1) {
                        handleChange("institutionId", null); // custom name will go in customInstitute
                        handleChange("institutionName", "Others"); // only for UI display
                      } else {
                        handleChange("institutionId", newValue.id); // store ID
                        handleChange(
                          "institutionName",
                          newValue.university_name
                        ); // for UI
                      }
                    }}
                    inputValue={searchInsQuery}
                    onInputChange={(e, newInputValue, reason) => {
                      setSearchInsQuery(newInputValue);
                      if (reason === "input") {
                        setInstitutes([]);
                        setInstPage(1);
                        setInstHasMore(true);
                        fetchInstitutes(1, getInstituteType(), searchInsQuery);
                      }
                    }}
                    // 👇 Append "Others" as a real option at the end
                    options={[
                      ...institutes,
                      { university_name: "Others", id: -1 },
                    ]}
                    isOptionEqualToValue={(option, value) =>
                      typeof value === "string"
                        ? option.university_name === value
                        : option.id === value?.id
                    }
                    getOptionLabel={(option) =>
                      typeof option === "string"
                        ? option
                        : option.university_name
                    }
                    filterOptions={(x) => x}
                    loading={loadingInstitutes}
                    ListboxProps={{
                      style: {
                        maxHeight: 200,
                        overflowY: "auto",
                        position: "relative",
                      },
                      onScroll: (event) => {
                        const { scrollTop, scrollHeight, clientHeight } =
                          event.currentTarget;
                        if (
                          scrollHeight - scrollTop <= clientHeight + 5 &&
                          instHasMore &&
                          !loadingInstitutes
                        ) {
                          fetchInstitutes(instPage + 1, getInstituteType());
                        }
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={renderLabel("Institute")}
                        placeholder="Search institute..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingInstitutes ? (
                                <CircularProgress size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={{
                          "& .MuiInputBase-input": {
                            color: "#9D0385",
                            fontWeight: "bold",
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => {
                      const isOthers = option.id === -1;
                      return (
                        <li
                          {...props}
                          key={option.id}
                          style={
                            isOthers
                              ? {
                                  position: "sticky",
                                  bottom: -8,
                                  background: "#fff",
                                  zIndex: 1,
                                }
                              : {}
                          }
                        >
                          {option.university_name}
                        </li>
                      );
                    }}
                  />
                </Grid>
                {formData.institutionName === "Others" && (
                  <Grid item xs={12} md={4}>
                    <TextField
                      className=""
                      label={renderLabel("Enter Institute Name")}
                      fullWidth
                      value={customInstitute}
                      onChange={(e) => setCustomInstitute(e.target.value)}
                      sx={{
                        "& .MuiInputBase-input": {
                          color: "#9D0385",
                          fontWeight: "bold",
                        },
                      }}
                    />
                  </Grid>
                )}
                {/* AFFILIATION */}
                <Grid item xs={12} md={4}>
                  <Autocomplete
                    value={formData.affiliation || null}
                    onChange={(e, newValue) => {
                      handleChange("affiliationId", newValue.id);
                      handleChange("affiliation", newValue.affiliation_name); // for UI
                    }}
                    inputValue={searchAffQuery}
                    onInputChange={(e, newInputValue, reason) => {
                      setSearchAffQuery(newInputValue);
                      if (reason === "input") {
                        setAffiliations([]);
                        setAffPage(1);
                        setHasMoreAff(true);
                        fetchAffiliations(1, newInputValue);
                      }
                    }}
                    options={affiliations} // 👈 no "Others"
                    isOptionEqualToValue={(option, value) =>
                      typeof value === "string"
                        ? option.affiliation_name === value
                        : option.id === value?.id
                    }
                    getOptionLabel={(option) =>
                      typeof option === "string"
                        ? option
                        : option.affiliation_name
                    }
                    filterOptions={(x) => x} // disable built-in filtering
                    loading={loadingAff}
                    ListboxProps={{
                      style: {
                        maxHeight: 200,
                        overflowY: "auto",
                        position: "relative",
                      },
                      onScroll: (event) => {
                        const { scrollTop, scrollHeight, clientHeight } =
                          event.currentTarget;
                        if (
                          scrollHeight - scrollTop <= clientHeight + 5 &&
                          hasMoreAff &&
                          !loadingAff
                        ) {
                          fetchAffiliations(affPage + 1, searchAffQuery); // ✅ pass search term
                        }
                      },
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={renderLabel("Affiliation/Board")}
                        placeholder="Search affiliation..."
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingAff ? (
                                <CircularProgress size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                        sx={{
                          "& .MuiInputBase-input": {
                            color: "#9D0385",
                            fontWeight: "bold",
                          },
                        }}
                      />
                    )}
                    renderOption={(props, option) => (
                      <li {...props} key={option.id}>
                        {option.affiliation_name}
                      </li>
                    )}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Number of Backlogs"
                    value={formData.numberOfBacklogs}
                    onChange={(e) =>
                      handleInputChange("numberOfBacklogs", e.target.value)
                    }
                    type="number"
                    sx={{
                      "& .MuiInputBase-input": {
                        color: "#9D0385",
                        fontWeight: "bold",
                      },
                    }}
                  />
                </Grid>
              </>
            )}

            {/* SHARED FIELDS */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>{renderLabel("Education Status")}</InputLabel>
                <Select
                  value={formData.educationStatus}
                  onChange={(e) =>
                    handleInputChange("educationStatus", e.target.value)
                  }
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "#9D0385",
                      fontWeight: "bold",
                    },
                  }}
                >
                  {educationStatuses.map((status) => (
                    <MenuItem key={status.id} value={status.name}>
                      {status.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Score Type</InputLabel>
                <Select
                  // value={scoreType}
                  value={formData.scoreType}
                  onChange={(e) => {
                    const newType = Number(e.target.value);

                    handleInputChange("scoreType", newType);

                    // Clear percentage / CGPA when switching type
                    handleInputChange("percentage", "");
                  }}
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "#9D0385",
                      fontWeight: "bold",
                    },
                  }}
                >
                  <MenuItem value={1}>Percentage</MenuItem>
                  <MenuItem value={2}>CGPA</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={formData.scoreType === 1 ? "Percentage" : "CGPA"}
                value={formData.percentage}
                onChange={(e) => {
                  let val = e.target.value;

                  // Allow only digits + one decimal
                  if (!/^\d*\.?\d*$/.test(val)) return;

                  // Allow max 2 decimals
                  if (val.includes(".")) {
                    const [int, dec] = val.split(".");
                    if (dec.length > 2) return;
                  }

                  let num = Number(val);

                  // Percentage validation
                  if (formData.scoreType === 1) {
                    if (val !== "" && (num < 0 || num > 100)) return;
                  }

                  // CGPA validation
                  if (formData.scoreType === 2) {
                    if (val !== "" && (num < 0 || num > 10)) return;
                  }

                  // Update
                  handleInputChange("percentage", val);
                }}
                sx={{
                  "& .MuiInputBase-input": {
                    color: "#9D0385",
                    fontWeight: "bold",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>{renderLabel("Year of Passing")} </InputLabel>
                <Select
                  value={formData.yearOfPassing}
                  onChange={(e) =>
                    handleInputChange("yearOfPassing", e.target.value)
                  }
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "#9D0385",
                      fontWeight: "bold",
                    },
                  }}
                >
                  {Array.from(
                    { length: 30 },
                    (_, i) => new Date().getFullYear() + 5 - i
                  ).map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* COUNTRY/STATE/CITY */}
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>{renderLabel("Country")}</InputLabel>
                <Select
                  value={formData.country}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      country: e.target.value,
                    }))
                  }
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "#9D0385",
                      fontWeight: "bold",
                    },
                  }}
                >
                  {loadingCountries && <MenuItem disabled>Loading...</MenuItem>}
                  {countries.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.country_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>{renderLabel("State")}</InputLabel>
                <Select
                  value={formData.state}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, state: e.target.value }))
                  }
                  disabled={!formData.country}
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "#9D0385",
                      fontWeight: "bold",
                    },
                  }}
                >
                  {loadingStates && <MenuItem disabled>Loading...</MenuItem>}
                  {states.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.state_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>City</InputLabel>
                <Select
                  value={formData.city}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, city: e.target.value }))
                  }
                  disabled={!formData.state}
                  sx={{
                    "& .MuiInputBase-input": {
                      color: "#9D0385",
                      fontWeight: "bold",
                    },
                  }}
                >
                  {loadingCities && <MenuItem disabled>Loading...</MenuItem>}
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.id}>
                      {city.city_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions className="p-6 pt-0">
          <Button
            variant="contained"
            onClick={handleSaveClick}
            className="bg-[#572575] hover:bg-[#572575] font-altisunisa text-base text-white px-8 py-2 rounded-lg flex items-center gap-2 w-[50%] justify-center mx-auto"
          >
            <Check />{" "}
            {editData ? "Update Information" : "Yes, Save Information"}
          </Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={showConfirm}
        title="Confirm Save"
        message={
          <>
            Are you sure you want to save your{" "}
            <strong>{getLevelName(formData.level)}</strong> academic details?
          </>
        }
        onConfirm={handleConfirm}
        onCancel={handleCancel}
      />

      {/* Snackbar for common error */}
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={4000}
        onClose={() => setErrorMessage("")}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setErrorMessage("")}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddEducationModal;
